// Slow Burn's building blocks, ported from design/slow-burn/boards/sb.js: the run header, the
// letters still standing (results), the Today card's depth track, the played column, the live row with a ghost of the
// next, the endgame fuse, the unprinted rows, the preview, the depth histogram, and how to play.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { SlowBurnInfo, SlowBurnRun } from '@wbwl/contract'
import { randomSlowBurn, scoreWord, SLOW_BURN } from '@wbwl/rules'
import { fmt } from '../lib/game.ts'
import { lettersStanding } from '../lib/slow-burn.ts'
import { Tile } from './game-ui.tsx'
import { Icon } from './Icon.tsx'
import { Sheet } from './shell.tsx'

type Gold = SlowBurnInfo['gold']
type Scoring = Pick<SlowBurnInfo, 'gold' | 'rulesVersion'>

/** The mode's mark: a fuse burning down. */
export function SlowBurnMark() {
  return <span className="sbmark" aria-hidden="true"><i /><i /><i /><i /></span>
}

/** Points (what the scoreboard ranks by), the row and words left. Words left turns red under 100. */
export function RunHead({ row, rows, points, left, label = 'Points', lead }: { row: number; rows: number; points: number; left: number | null; label?: string; lead?: ReactNode }) {
  return (
    <div className="runhead">
      {lead}
      <div><div className="lbl">{label}</div><div className="num">{fmt(points)}</div></div>
      <div><div className="lbl">Row</div><div className="sub">{row}<small>/{rows}</small></div></div>
      {left !== null && <div className="left"><div className="lbl">Words left</div><div className={`sub${left < 100 ? ' low' : ''}`}>{fmt(left)}</div></div>}
    </div>
  )
}

/** The letters a finished run never burned, A to Z, gold ones in gold: what was left to play with. */
export function StandingLetters({ gold, ashes, rulesVersion }: { gold: Gold; ashes: readonly string[]; rulesVersion: number }) {
  const standing = lettersStanding(ashes)
  return (
    <div className="standing">
      <div className="standing-head"><span>{standing.length === 1 ? '1 letter' : `${standing.length} letters`} still standing</span></div>
      <div className="standing-tiles" aria-label={`Letters you still had: ${standing.join(' ')}`}>
        {standing.map((l) => <Tile key={l} l={l} k={`typed${gold[l] ? ' gold' : ''}`} size={30} gold={gold} rv={rulesVersion} gb={false} />)}
      </div>
    </div>
  )
}

/** Row 25 is a perfect run (row 26 can't be played), so the depth track has 25 segments. */
const TRACK_ROWS = SLOW_BURN.rows - 1

/**
 * The Today card's depth track (design/slow-burn/boards/TodayCard.html, option A): one segment per
 * row, lit as far as you got, faint embers on to the day's deepest run. `now` is the row you're on
 * while a run is going; `deepest` is null until anyone finishes.
 */
export function DepthTrack({ you, deepest, now = 0 }: { you: number; deepest: number | null; now?: number }) {
  // A label sits centred on its row, except near either end, where it lines up with that end so it stays on the card.
  const at = (row: number): CSSProperties => {
    const f = (row - 0.5) / TRACK_ROWS
    const transform = f > 0.75 ? 'translateX(calc(-100% + 6px))' : f < 0.25 ? 'translateX(-6px)' : undefined
    return { left: `${f * 100}%`, transform }
  }
  const top = deepest ?? 0
  const segs: ReactNode[] = []
  for (let r = 1; r <= TRACK_ROWS; r++) {
    if (r === now) segs.push(<i key={r} className="now" />)
    else if (r <= you) segs.push(r === you && !now ? <i key={r} className="tip" /> : <i key={r} style={{ background: `rgba(242, 88, 59, ${(0.35 + 0.6 * (r / you)).toFixed(2)})` }} />)
    else if (r <= top) segs.push(<i key={r} className="reach" />)
    else segs.push(<i key={r} />)
  }
  const mine = now || you
  const tied = !now && you > 0 && you === top
  return (
    <div className={`depth${mine ? '' : ' bare'}`} aria-label={`${mine ? `${now ? 'On' : 'Reached'} row ${mine}` : 'Not started'}${top ? `; today's deepest run reached row ${top}` : ''}`}>
      <div className="dtrack">{segs}</div>
      {tied ? <span className="pin up" style={at(you)}><Icon name="crown" />You · deepest</span> : <>
        {mine > 0 && <span className="pin up" style={at(mine)}>{now ? `Row ${now}` : `You · ${you}`}</span>}
        {top > 0 && <span className="pin down" style={at(top)}>Deepest · {top}</span>}
      </>}
      <span className="pin down end start">1</span>
      {top < TRACK_ROWS - 3 && <span className="pin down end finish">{TRACK_ROWS}</span>}
    </div>
  )
}

/** A played word with its burned letter in red. */
export function RunWord({ gold, rulesVersion, word, col, size = 28, gap = 4, flare = false }: Scoring & { word: string; col: number; size?: number; gap?: number; flare?: boolean }) {
  return (
    <div className="runword" style={{ gap }}>
      {[...word].map((l, c) => (
        <Tile key={c} l={l} k={['played', gold[l] ? 'gold' : '', c === col ? 'burned' : '', c === col && flare ? 'flare pop' : ''].join(' ')} size={size} gold={gold} gb={false} pt={size >= 34} fl={size >= 30} rv={rulesVersion} />
      ))}
    </div>
  )
}

/**
 * The last few played rows, older ones fading; the rest scroll out of view. `liveTile` is the live
 * row's tile size: each played row is as wide as the live row, so the row numbers and scores sit in
 * its columns while the smaller words stay centred.
 */
export function PlayedColumn({ info, run, show = 5, size = 28, liveTile, flareLast = false }: { info: SlowBurnInfo; run: SlowBurnRun; show?: number; size?: number; liveTile: number; flareLast?: boolean }) {
  const n = run.rows.length
  const from = Math.max(0, n - show)
  const empty = show - (n - from)
  return (
    <div className="column" style={{ width: LIVE_GUTTERS + 5 * liveTile + 4 * LIVE_GAP }}>
      {/* Always `show` slots (empty ones on top) and the label's line, so the live row never moves. */}
      <div className="more" style={from ? undefined : { visibility: 'hidden' }}>{from} earlier row{from === 1 ? '' : 's'}</div>
      {Array.from({ length: empty }, (_, j) => (
        <div key={`e${j}`} className="crow empty" aria-hidden="true">
          <span className="n" />
          <div className="runword" style={{ gap: 4 }}>{[0, 1, 2, 3, 4].map((c) => <Tile key={c} k="slot" size={size} />)}</div>
          <span className="s" />
        </div>
      ))}
      {run.rows.slice(from).map((w, i) => {
        const r = from + i
        // Older rows fade fast: the slot above the live row is solid, the top slot barely there.
        const fade = 0.12 + 0.88 * ((empty + i + 1) / show) ** 2
        return (
          <div key={r} className="crow" style={{ opacity: fade }}>
            <span className="n">{r + 1}</span>
            <RunWord gold={info.gold} rulesVersion={info.rulesVersion} word={w} col={run.cols[r]} size={size} flare={flareLast && r === n - 1} />
            <span className="s">{run.scores.rows[r].total}</span>
          </div>
        )
      })}
    </div>
  )
}

/** The live row's tile gap, and its row number and score columns with the gaps beside them (see app.css). */
const LIVE_GAP = 6
const LIVE_GUTTERS = 44 + 8 + 8 + 44

/** The row you're on, and below it a ghost of the next showing only where its flame sits. */
export function LiveRows({ info, run, typing, size, invalid, shake, under }: { info: SlowBurnInfo; run: SlowBurnRun; typing: string; size: number; invalid?: boolean; shake?: boolean; under?: ReactNode }) {
  const r = run.rows.length
  const col = run.cols[r]
  const nextCol = run.cols[r + 1]
  let rs: ReactNode = <div className="rs"><b>—</b></div>
  if (invalid) rs = <div className="rs"><b style={{ color: 'var(--burn)' }}>✕</b></div>
  else if (typing.length === 5) {
    const s = scoreWord(typing, info)
    rs = <div className="rs on"><b>{s.total}</b><small>{s.pts}×{s.mult}</small></div>
  }
  return (
    <div className="liverows">
      <div className={`liverow${shake ? ' shake' : ''}`}>
        <div className="ls">▸{r + 1}</div>
        <div className="tiles">
          {[0, 1, 2, 3, 4].map((c) => {
            const l = typing[c]
            return <Tile key={`${c}${l ?? ''}`} l={l} k={[l ? 'typed pop' : 'cur', l && info.gold[l] ? 'gold' : '', c === col ? 'burn' : ''].join(' ')} size={size} gold={info.gold} rv={info.rulesVersion} gb={!(l && typing.indexOf(l) < c)} />
          })}
        </div>
        {rs}
      </div>
      {under}
      {r + 1 < info.rows && (
        <div className="nextrow" aria-label={nextCol === undefined ? 'Next row' : `Next row: the flame is on letter ${nextCol + 1}`}>
          <div className="ls">{r + 2}</div>
          <div className="tiles">{[0, 1, 2, 3, 4].map((c) => <Tile key={c} k={['ghost', c === nextCol ? 'burn' : ''].join(' ')} size={size} />)}</div>
          <div className="rs">Next</div>
        </div>
      )}
    </div>
  )
}

/**
 * The endgame fuse under the live row: a cord as wide as the tiles that burns down from the right,
 * a flame at its tip, and the seconds left in the score column, red for the last ten. Timed from
 * the server's `msLeft` when the run arrived, so the device's clock doesn't matter; `onOut` fires
 * once when it reaches zero. The slot keeps its height without a fuse, so lighting it moves nothing.
 */
export function RunFuse({ fuse, size, onOut }: { fuse: SlowBurnRun['fuse']; size: number; onOut(): void }) {
  const deadline = useMemo(() => (fuse ? performance.now() + fuse.msLeft : null), [fuse])
  const [now, setNow] = useState(() => performance.now())
  const onOutRef = useRef(onOut)
  onOutRef.current = onOut
  useEffect(() => {
    if (deadline === null) return
    let fired = false
    let frame = 0
    const tick = (t: number) => {
      setNow(t)
      if (t < deadline) frame = requestAnimationFrame(tick)
      else if (!fired) { fired = true; onOutRef.current() }
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [deadline])
  if (!fuse || deadline === null) return <div className="fuse-slot" />
  const left = Math.max(0, deadline - now)
  const pct = (left / (fuse.seconds * 1000)) * 100
  const secs = Math.ceil(left / 1000)
  return (
    <div className={`fuse-slot runfuse${secs <= 10 ? ' low' : ''}`} role="timer" aria-label={`${secs} seconds to play this row`}>
      <div className="ls" aria-hidden="true"><Icon name="clock" /></div>
      <div className="cord" style={{ width: 5 * size + 4 * LIVE_GAP }}>
        <i style={{ width: `${pct}%` }} />
        {/* A small flame at the burning tip: an orange flame with a yellow core, flickering. */}
        <b className="spark" style={{ left: `${pct}%` }} aria-hidden="true"><Icon name="flame" className="outer" /><Icon name="flame" className="core" /></b>
      </div>
      <div className="rs">{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}</div>
    </div>
  )
}

/** The rows after the next: greyed out until you reach them. */
export function Fog({ row, rows }: { row: number; rows: number }) {
  if (row + 2 >= rows) return null
  return (
    <div className="fog">
      <div className="bars" aria-hidden="true"><i /><i /><i /></div>
    </div>
  )
}

export function RunPreview({ info, word, col, invalid }: { info: SlowBurnInfo; word: string; col: number | undefined; invalid?: boolean }) {
  if (!word) return <div className="preview"><span className="math sb-hint">Type a word · the letter on <Icon name="flame" /> burns</span></div>
  if (invalid) return <div className="preview"><span className="math">{word} isn't in the word list</span><span className="eq faint" style={{ marginLeft: 'auto' }}>✕</span></div>
  const s = scoreWord(word, info)
  const burns = col === undefined ? undefined : word[col]
  return (
    <div className="preview">
      <span className="math"><b>{s.pts}</b> pts × <em>{s.mult}</em></span>
      {word.length < 5 ? <span className="eq faint" style={{ marginLeft: 'auto', fontSize: 15 }}>so far</span> : <span className="eq" style={{ marginLeft: 'auto' }}>{s.total}</span>}
      {burns && <span className="chip burn" style={{ height: 26 }}><Icon name="flame" />{burns} burns</span>}
    </div>
  )
}

/** Where today's runs ended, one bar per row; yours in red. */
export function DepthHistogram({ bins, you, height = 52 }: { bins: readonly number[]; you: number | null; height?: number }) {
  const max = Math.max(1, ...bins)
  const n = Math.max(bins.length, SLOW_BURN.rows)
  return (
    <div className="dhist">
      <div className="bars" style={{ height }}>
        {Array.from({ length: n }, (_, i) => {
          const v = bins[i] ?? 0
          const cls = you === i + 1 ? 'you' : you !== null && i + 1 > you ? 'past' : ''
          return <i key={i} className={cls} style={{ height: Math.max(2, Math.round((v / max) * height)) }} />
        })}
      </div>
      <div className="axis"><span>row 1</span><span>{n}</span></div>
    </div>
  )
}

// The design boards' sample pattern, for the how-to's example.
const SAMPLE = randomSlowBurn('slow-burn-sample')

export function SlowBurnHowTo({ open, onClose }: { open: boolean; onClose(): void }) {
  return (
    <Sheet open={open} onClose={onClose} label="How to play Slow Burn">
      <div className="howto">
        <div className="howto-head">
          <h2 className="h-serif">Slow <i className="burn-em">Burn</i></h2>
          <button type="button" className="ibtn boxed" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <p className="muted small">One run a day, a new burn order every day, the same for everyone. How many points can you score before the fuse runs out?</p>
        <ol className="steps">
          <li><h3>Play a five-letter word on each row</h3><p>Points work as in the daily game: letter points × your gold multiplier.</p></li>
          <li>
            <h3>Every row has one flame tile</h3>
            <p>The letter you put there <span className="burn-word">burns</span>: it's gone for the rest of the run. You choose which letter to lose.</p>
            <div className="ex"><RunWord gold={SAMPLE.gold} rulesVersion={SAMPLE.rulesVersion} word="INKED" col={2} size={34} /><span className="eqn">No more K today</span></div>
          </li>
          <li><h3>You see one row ahead</h3><p>The next row's flame shows as a ghost. Everything after that prints when you reach it.</p></li>
          <li><h3>Watch the fuse at the end</h3><p>Once fewer than 100 words fit, a fuse lights under your row: 60 seconds to play each word, or the run ends with the points you have.</p></li>
          <li><h3>The run ends when no word fits</h3><p>Or end your run whenever you like, and any run still going at midnight Pacific ends with the day. Row 25 is a perfect run: by row 26 only one letter is left.</p></li>
          <li><h3>Most points wins</h3><p>The scoreboard ranks by points. Every row you survive is another word to score, and the row you reached breaks ties.</p></li>
        </ol>
        <button type="button" className="btn ink block" onClick={onClose}>Got it</button>
      </div>
    </Sheet>
  )
}
